/**
 * Complete Bundles — Turtle preset kits (single-select).
 *
 * Responsibilities:
 * - Select one Shopify Bundle product, write `input[name=id]`, sync price / gallery / buy-buttons stock UI
 * - Mutually exclusive with Build Your Own via `turtle:kit-selection`
 *
 * Events:
 * - Dispatches `turtle:kit-selection` with detail `{ source: "complete-bundles" }`
 * - Listens for the same event; clears selection when source is `build-your-own`
 *
 * DOM:
 * - Host: `<complete-bundles data-product-form-id="...">`
 * - Cards: `.complete-bundles__card` (data attrs from `complete-bundles-card` snippet)
 *
 * Public API:
 * - `selectCard(card, { silent })` — silent skips mutual-exclusion dispatch (BYO visual sync)
 * - `selectByProductId(productId, { silent })` — select by product id; clear if not found
 * - `clearSelection()` — clear UI only; does not rewrite form variant id
 *
 * Does not modify theme.js.liquid.
 */
if (!customElements.get('complete-bundles')) {
  /** Set true to enable console debug logs */
  const DEBUG = false;

  /** Shared mutual-exclusion event with Build Your Own */
  const KIT_SELECTION_EVENT = 'turtle:kit-selection';

  class CompleteBundles extends HTMLElement {
    disconnectedCallback() {
      document.removeEventListener(KIT_SELECTION_EVENT, this._onKitSelection);
      this._quantityInput?.removeEventListener("input", this._onQuantityChange);
      this._quantityInput?.removeEventListener("change", this._onQuantityChange);
      this._quantityInput = null;
      this._onQuantityChange = null;
    }

    log(label, ...args) {
      if (!DEBUG) return;
      console.log(`[Complete Bundles · ${label}]`, ...args);
    }

    connectedCallback() {
      this.log('mounted', {
        cardCount: this.querySelectorAll('.complete-bundles__card').length,
      });
      this._onKitSelection = this.onKitSelection.bind(this);
      this.initSelection();
      this.bindQuantityChange();
      this.bindKitSelection();

      requestAnimationFrame(() => {
        this.classList.add('is-ready');
      });
    }

    /** Clear this component when Build Your Own is selected */
    bindKitSelection() {
      document.removeEventListener(KIT_SELECTION_EVENT, this._onKitSelection);
      document.addEventListener(KIT_SELECTION_EVENT, this._onKitSelection);
    }

    onKitSelection(event) {
      if (event.detail?.source !== 'build-your-own') {
        return;
      }

      this.clearSelection();
    }

    /** Notify Build Your Own that a bundle was selected */
    dispatchKitSelection() {
      document.dispatchEvent(
        new CustomEvent(KIT_SELECTION_EVENT, {
          detail: { source: 'complete-bundles' },
        })
      );
    }

    /** Clear selected state and collapse details (does not change form id) */
    clearSelection() {
      const cards = [...this.querySelectorAll('.complete-bundles__card')];

      cards.forEach((card) => {
        card.classList.remove('is-selected');
        this.setDetailsExpanded(card, false);
      });

      this.log('selection cleared');
    }

    /**
     * Select the card for productId (UI + form write-back); clear if no match.
     * @param {string|number} productId
     * @param {{ silent?: boolean }} [options] silent skips mutual-exclusion dispatch
     * @returns {boolean}
     */
    selectByProductId(productId, { silent = false } = {}) {
      if (!productId) {
        this.clearSelection();
        return false;
      }

      const card = [...this.querySelectorAll('.complete-bundles__card')].find(
        (item) => String(item.dataset.productId) === String(productId)
      );

      if (!card) {
        this.clearSelection();
        return false;
      }

      this.selectCard(card, { silent });
      return true;
    }

    getProductForm() {
      const productFormId = this.dataset.productFormId;

      if (!productFormId) {
        return null;
      }

      return this.ownerDocument.forms[productFormId] || null;
    }

    /** Write the card variant id into the main form and form=-linked id inputs */
    updateFormVariantId(card) {
      const variantId = card?.dataset?.variantId;
      const productFormId = this.dataset.productFormId;

      if (!variantId || !productFormId) {
        this.log('skip variant id update', {
          reason: !variantId ? 'card missing variantId' : 'missing productFormId',
          variantId,
          productFormId,
        });
        return;
      }

      const formInputs = [
        ...this.ownerDocument.querySelectorAll(`form#${productFormId} input[name="id"]`),
        ...this.ownerDocument.querySelectorAll(`input[name="id"][form="${productFormId}"]`),
      ];

      if (!formInputs.length) {
        this.log('variant id update failed', { reason: 'id input not found', productFormId, variantId });
        return;
      }

      formInputs.forEach((input) => {
        input.value = variantId;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      this.log('variant id updated', {
        variantId,
        productFormId,
        inputCount: formInputs.length,
      });
    }

    getRequestedQuantity() {
      const productForm = this.getProductForm();
      const quantityInput = productForm?.querySelector('input[name="quantity"]');
      const quantity = Number.parseInt(quantityInput?.value || '1', 10);

      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    isInventorySufficient(card) {
      if (!card) {
        return false;
      }

      const isAvailable = card.dataset.variantAvailable === 'true';

      if (!isAvailable) {
        return false;
      }

      const inventoryManagement = card.dataset.variantInventoryManagement;
      const inventoryPolicy = card.dataset.variantInventoryPolicy;
      const inventoryQuantity = Number.parseInt(card.dataset.variantInventoryQuantity || '', 10);
      const requestedQuantity = this.getRequestedQuantity();

      if (
        inventoryManagement &&
        inventoryPolicy !== 'continue' &&
        Number.isFinite(inventoryQuantity)
      ) {
        return requestedQuantity <= inventoryQuantity;
      }

      return true;
    }

    notifyBuyButtons(card) {
      const productForm = this.getProductForm();

      if (!productForm) {
        return;
      }

      const isInventorySufficient = this.isInventorySufficient(card);

      // Let buy-buttons handle variant:change first, then override with our stock check
      // so theme internal logic does not wipe the result.
      const update = () => this.updateBuyButtonsAvailability(isInventorySufficient);
      if (typeof queueMicrotask === "function") {
        queueMicrotask(update);
      } else {
        setTimeout(update, 0);
      }

      this.log('notify buy-buttons stock', {
        variantId: card?.dataset?.variantId,
        requestedQuantity: this.getRequestedQuantity(),
        isInventorySufficient,
      });
    }

    updateBuyButtonsAvailability(isInventorySufficient) {
      const productFormId = this.dataset.productFormId;

      if (!productFormId) {
        return;
      }

      const buyButtons = [
        ...this.ownerDocument.querySelectorAll(`buy-buttons[form="${productFormId}"]`),
      ];

      if (!buyButtons.length) {
        return;
      }

      buyButtons.forEach((buyButtonsEl) => {
        const addToCartButton = buyButtonsEl.querySelector('button[type="submit"]');
        const paymentButton = buyButtonsEl.querySelector(".shopify-payment-button");

        if (!addToCartButton) {
          return;
        }

        const addToCartButtonText =
          addToCartButton.getAttribute("is") === "custom-button"
            ? addToCartButton.firstElementChild
            : addToCartButton;

        addToCartButton.classList.remove("button--secondary", "button--subdued");
        addToCartButton.disabled = !isInventorySufficient;

        addToCartButton.classList.add(
          isInventorySufficient
            ? paymentButton || buyButtonsEl.hasAttribute("force-secondary-button")
              ? "button--secondary"
              : "button"
            : "button--subdued"
        );

        addToCartButtonText.innerHTML = isInventorySufficient
          ? (buyButtonsEl.getAttribute("template") || "").includes("pre-order")
            ? window.themeVariables.strings.preOrderButton
            : window.themeVariables.strings.addToCartButton
          : window.themeVariables.strings.soldOutButton;

        if (paymentButton) {
          paymentButton.style.display = isInventorySufficient ? "block" : "none";
        }
      });
    }

    bindQuantityChange() {
      const productForm = this.getProductForm();
      const quantityInput = productForm?.querySelector('input[name="quantity"]');

      if (!quantityInput) {
        return;
      }

      this._quantityInput = quantityInput;
      this._onQuantityChange = () => {
        const selectedCard = this.querySelector('.complete-bundles__card.is-selected');

        if (selectedCard) {
          this.notifyBuyButtons(selectedCard);
        }
      };

      // Avoid duplicate listeners
      this._quantityInput.removeEventListener("input", this._onQuantityChange);
      this._quantityInput.removeEventListener("change", this._onQuantityChange);
      this._quantityInput.addEventListener("input", this._onQuantityChange);
      this._quantityInput.addEventListener("change", this._onQuantityChange);
    }

    getCurrencyFormat() {
      const settings = window.themeVariables?.settings;

      if (!settings) {
        return '${{amount}}';
      }

      return settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat;
    }

    formatMoneyLocal(cents, format = '') {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }

      const formatString = format || this.getCurrencyFormat();
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = formatString.match(placeholderRegex);

      if (!match) {
        return (Number(cents) / 100).toFixed(2);
      }

      const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
        if (Number.isNaN(number) || number == null) {
          return '0';
        }

        number = (number / 100).toFixed(precision);
        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        const centsAmount = parts[1] ? decimal + parts[1] : '';

        return dollarsAmount + centsAmount;
      };

      let value = '';

      switch (match[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_space_separator':
          value = formatWithDelimiters(cents, 2, ' ', '.');
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'", '.');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_no_decimals_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 0, "'");
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }

      return formatString.replace(placeholderRegex, value);
    }

    getFormattedPrice(card, type = 'sale') {
      const formatted =
        type === 'compare'
          ? card?.dataset?.variantCompareAtPriceFormatted
          : card?.dataset?.variantPriceFormatted;

      if (formatted) {
        return formatted;
      }

      const cents =
        type === 'compare'
          ? Number.parseInt(card?.dataset?.variantCompareAtPrice || '0', 10)
          : Number.parseInt(card?.dataset?.variantPrice || '', 10);

      return this.formatMoneyLocal(cents);
    }

    getProductPriceContainer() {
      return (
        this.closest('.product-info')?.querySelector('.product-info__price') ||
        this.ownerDocument.querySelector('.product-info__price')
      );
    }

    getProductGallery() {
      const productFormId = this.dataset.productFormId;

      if (!productFormId) {
        return null;
      }

      return (
        this.ownerDocument.querySelector(`product-gallery-v2[form="${productFormId}"]`) ||
        this.ownerDocument.querySelector(`product-gallery[form="${productFormId}"]`)
      );
    }

    /**
     * Parse and cache `#turtle-kit-catalog` for Gallery v2 media payloads.
     * @returns {Record<string, object>}
     */
    getCatalog() {
      if (this._catalog) {
        return this._catalog;
      }

      const script = this.ownerDocument.getElementById('turtle-kit-catalog');

      if (!script) {
        this._catalog = {};
        return this._catalog;
      }

      try {
        this._catalog = JSON.parse(script.textContent || '{}');
      } catch (error) {
        this.log('catalog parse failed', error);
        this._catalog = {};
      }

      return this._catalog;
    }

    updateProductGallery(card) {
      const gallery = this.getProductGallery();

      if (!gallery || gallery.tagName !== 'PRODUCT-GALLERY-V2') {
        this.log('skip gallery switch', {
          reason: !gallery ? 'gallery not found' : 'not gallery v2',
        });
        return;
      }

      const productId = card?.dataset?.productId;
      const entry = productId ? this.getCatalog()[productId] : null;
      const payload = {
        mainImage: entry?.mainImage ?? null,
        modelsImages: entry?.modelsImages ?? [],
        inTheBoxImages: entry?.inTheBoxImages ?? [],
      };

      const apply = () => {
        if (typeof gallery.setBundleMedia === 'function') {
          gallery.setBundleMedia(payload);
        }
      };

      if (customElements.get('product-gallery-v2')) {
        apply();
      } else {
        customElements.whenDefined('product-gallery-v2').then(apply);
      }

      this.log('gallery v2 switched', {
        productId,
        hasMain: Boolean(entry?.mainImage),
        models: entry?.modelsImages?.length ?? 0,
        inTheBox: entry?.inTheBoxImages?.length ?? 0,
      });
    }

    updateProductPrice(card) {
      const productFormId = this.dataset.productFormId;
      const price = Number.parseInt(card?.dataset?.variantPrice || '', 10);
      const compareAtPrice = Number.parseInt(card?.dataset?.variantCompareAtPrice || '0', 10);

      if (!productFormId || !Number.isFinite(price)) {
        this.log('skip price update', {
          reason: !productFormId ? 'missing productFormId' : 'missing price',
          productFormId,
          price,
        });
        return;
      }

      const isOnSale = compareAtPrice > price;
      const saleText = this.getFormattedPrice(card, 'sale');
      const compareText = this.getFormattedPrice(card, 'compare');

      const salePrices = [...this.ownerDocument.querySelectorAll(`sale-price[form="${productFormId}"]`)];
      const compareAtPrices = [
        ...this.ownerDocument.querySelectorAll(`compare-at-price[form="${productFormId}"]`),
      ];

      if (!salePrices.length) {
        this.log('price update failed', {
          reason: 'sale-price not found',
          productFormId,
        });
        return;
      }

      salePrices.forEach((salePrice) => {
        if (salePrice?.lastChild) {
          salePrice.lastChild.replaceWith(
            document.createRange().createContextualFragment(saleText)
          );
          salePrice.classList.toggle('text-on-sale', isOnSale);
        }
      });

      compareAtPrices.forEach((compareAtPriceEl) => {
        if (compareAtPriceEl?.lastChild) {
          compareAtPriceEl.lastChild.replaceWith(
            document.createRange().createContextualFragment(compareText)
          );
          compareAtPriceEl.hidden = !isOnSale;
        }
      });

      const priceContainer = this.getProductPriceContainer();
      const onSaleBadge = priceContainer?.querySelector(
        `on-sale-badge[form="${productFormId}"]`
      );

      if (onSaleBadge) {
        if (isOnSale) {
          const savings = this.formatMoneyLocal(compareAtPrice - price);
          const discountBadge = window.themeVariables?.strings?.discountBadge;

          if (discountBadge) {
            onSaleBadge.innerHTML = discountBadge.replace('@@', savings);
          }

          onSaleBadge.hidden = false;
        } else {
          onSaleBadge.hidden = true;
        }
      }

      const paypalMessage = priceContainer?.querySelector?.('[data-pp-message]');

      if (paypalMessage) {
        paypalMessage.setAttribute('data-pp-amount', (price / 100).toFixed(2));
      }

      this.log('price updated', {
        variantId: card.dataset.variantId,
        price,
        compareAtPrice,
        isOnSale,
        saleText,
        updatedCount: salePrices.length,
      });
    }

    setDetailsExpanded(card, expanded) {
      const details = card.querySelector('.complete-bundles__card-details');
      const toggle = card.querySelector('.complete-bundles__card-toggle');

      if (!details) {
        return;
      }

      details.classList.toggle('is-expanded', expanded);
      toggle?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    /**
     * Select a bundle card: write-back variant / price / gallery / stock; optionally dispatch.
     * @param {HTMLElement} card
     * @param {{ silent?: boolean }} [options] silent=true for BYO visual sync without clearing BYO
     */
    selectCard(card, { silent = false } = {}) {
      const cards = [...this.querySelectorAll('.complete-bundles__card')];

      cards.forEach((otherCard) => {
        if (otherCard === card) {
          return;
        }

        otherCard.classList.remove('is-selected');
        this.setDetailsExpanded(otherCard, false);
      });

      card.classList.add('is-selected');
      this.setDetailsExpanded(card, true);
      this.updateFormVariantId(card);
      this.updateProductPrice(card);
      this.updateProductGallery(card);
      this.notifyBuyButtons(card);

      if (!silent) {
        this.dispatchKitSelection();
      }

      this.log('card selected', {
        productId: card.dataset.productId,
        silent,
      });
    }

    initSelection() {
      const cards = [...this.querySelectorAll('.complete-bundles__card')];

      this.log('init selection', { cardCount: cards.length });

      if (!cards.length) {
        this.log('hide component', 'no bundle cards rendered');
        this.hidden = true;
        return;
      }

      cards.forEach((card) => {
        const toggle = card.querySelector('.complete-bundles__card-toggle');

        if (!toggle) {
          return;
        }

        toggle.addEventListener('click', () => {
          if (card.classList.contains('is-selected')) {
            this.log('card click', {
              productId: card.dataset.productId,
              action: 'keep selected',
            });
            return;
          }

          this.selectCard(card);
        });
      });
    }
  }

  customElements.define('complete-bundles', CompleteBundles);
}
